// The parts of a Zero-trust tab (impire-hq 02-DESIGN/03-console/01-the-console.md
// § Settings: the Zero-trust tab; 02-DESIGN/02-secrets/03-escrow.md § what the
// console shows), shared by Settings' tab over the workspace's own shares and
// a child's tab under Workspaces over the parent's share of the child's keys
// (spec 061): the tab's state, with the share Collect makes kept only while
// its entry is current; the set and its id; the three shares in a table; an
// open recovery; Collect's one-time share and Present, each in the tab's
// place (§ conventions: a form). Nothing here speaks to the bus itself: each
// tab hands in the calls it builds, so the verb inventory reads every subject
// where it is built. Nothing here shows a share after the moment it is
// collected, and nothing shows a key, ever.

import { useState, type ReactNode } from "react";

import {
  Button,
  Field,
  NotAllowed,
  Notice,
  OnceValue,
  TextArea,
  TextInput,
  useLoad,
  when,
  type Load,
} from "../../ui/components.tsx";
import { FormPage, useFormState } from "../../ui/form.tsx";
import { Ident } from "../../ui/ident.tsx";
import { ConfirmDialog, Table, type Column, type MenuEntry } from "../../ui/table.tsx";
import {
  checkShare,
  EscrowAction,
  Slot,
  SlotState,
  type EscrowCollectReply,
  type EscrowPresenter,
  type EscrowPresentReply,
  type EscrowSlotStatus,
  type EscrowStatus,
} from "../../wire/escrow.ts";
import {
  ceremonyTitle,
  checkAnswer,
  COLLECT_FORM,
  COLLECT_HINT,
  collectPushes,
  PRESENT_FORM,
  presentConsequence,
  SET_ID_HINT,
  setLine,
  shareStep,
  shownShare,
  SLOT_ORDER,
  slotName,
  type Holders,
  type ZeroTrustForm,
} from "./zero-trust.ts";

/** ShareAct is an act on the tab's own share, which a child's tab takes too: Collect and Present. */
export type ShareAct = typeof EscrowAction.collect | typeof EscrowAction.present;

/**
 * ZeroTrustState is a Zero-trust tab's state, held by the page so that
 * Collect can be its primary action: the status, the share Collect just made
 * while its entry is current, and the acts on the tab's own share.
 */
export interface ZeroTrustState {
  st: Load<EscrowStatus>;
  busy: boolean;
  err: unknown;
  collected: EscrowCollectReply | undefined;
  closeCollected: () => void;
  collect: () => void;
  present: (body: unknown) => Promise<unknown>;
  /** run takes an act — busy while it runs, the status read again after it. */
  run: (act: () => Promise<unknown>) => Promise<unknown>;
}

/**
 * useShares is a Zero-trust tab's state over one workspace's shares: `read`
 * reads the status, `call` speaks Collect and Present. Each caller builds
 * its own subjects — the workspace's own, or a child's as its parent — so
 * the verb inventory reads every one.
 */
export function useShares(
  key: string,
  read: () => Promise<EscrowStatus>,
  call: (action: ShareAct, body: unknown) => Promise<unknown>,
): ZeroTrustState {
  const [tick, setTick] = useState(0);
  const [st] = useLoad(key, read, tick, { keep: true });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const [held, setHeld] = useState<EscrowCollectReply | undefined>(undefined);
  const form = useFormState<ZeroTrustForm>();
  // The share is shown once: leaving its entry — Back as much as Done —
  // drops it for good, so Forward finds nothing to show again.
  const [was, setWas] = useState(form.open);
  if (was !== form.open) {
    const next = shareStep(was, form.open, held);
    setWas(form.open);
    if (next !== held) {
      setHeld(next);
    }
  }
  const collected = shownShare(form.open, held);
  const run = (act: () => Promise<unknown>): Promise<unknown> => {
    setBusy(true);
    setErr(undefined);
    return act().finally(() => {
      setBusy(false);
      setTick((t) => t + 1);
    });
  };
  return {
    st,
    busy,
    err,
    collected,
    closeCollected: () => {
      setHeld(undefined);
      if (collected !== undefined) {
        form.close();
      }
    },
    collect: () => {
      run(() => call(EscrowAction.collect, undefined)).then(
        (r) => {
          setHeld(r as EscrowCollectReply);
          // On an entry that already names Collect — a refresh onto a spent
          // one — the share is drawn on that entry rather than on a second
          // one of the same name, so Back always leaves it.
          if (collectPushes(form.open)) {
            form.show(COLLECT_FORM);
          }
        },
        (e: unknown) => {
          setErr(e);
        },
      );
    },
    present: (body) => run(() => call(EscrowAction.present, body)),
    run,
  };
}

/**
 * zeroTrustFormDrawn reports whether the tab draws a form in its place now:
 * the share *Collect* just made, or *Present* over a set to present to. A
 * form named in the history entry that cannot be drawn — a refresh or
 * Forward onto a spent *Collect* — leaves the tab as it is, its action too.
 */
export function zeroTrustFormDrawn(
  open: string | undefined,
  collected: boolean,
  status: { set?: string } | undefined,
): boolean {
  return collected || (open === PRESENT_FORM && status?.set !== undefined);
}

/** waiting reports whether the tab's own share — `mine` — waits to be collected. */
export function waiting(st: EscrowStatus, mine: string): boolean {
  return st.slots.some((x) => x.slot === mine && x.state === SlotState.waiting);
}

/** CollectAction is the tab's primary action while its own share — `mine` — waits, or nothing. */
export function CollectAction({ z, mine = Slot.admin }: { z: ZeroTrustState; mine?: string }) {
  if (z.st.state !== "ready" || z.st.value.set === undefined || !waiting(z.st.value, mine)) {
    return null;
  }
  return (
    <Button kind="primary" disabled={z.busy} onClick={z.collect}>
      Collect
    </Button>
  );
}

/** PresentAct is *Present* with its line: when a person needs it. */
export function PresentAct({
  busy,
  onPresent,
  line,
}: {
  busy: boolean;
  onPresent: () => void;
  line: string;
}) {
  return (
    <div>
      <Button kind="default" disabled={busy} onClick={onPresent}>
        Present
      </Button>
      <p className="small muted">{line}</p>
    </div>
  );
}

/**
 * OneTimeShare is the share *Collect* just made, in the tab's place: the
 * one-time value with its copy button, and the line that it is not shown
 * again. Done — or Back — is the end of it.
 */
export function OneTimeShare({
  title,
  reply,
  onClose,
  children,
}: {
  title: string;
  reply: EscrowCollectReply;
  onClose: () => void;
  /** The line under the value. */
  children: ReactNode;
}) {
  return (
    <FormPage
      title={title}
      heading="h2"
      onClose={onClose}
      foot={
        <Button kind="primary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="stack">
        <OnceValue label={`Set ${reply.set}`} value={reply.share} />
        <p className="small">{children}</p>
      </div>
    </FormPage>
  );
}

/**
 * ShareSet is the set's line — when it was issued and why — and its id,
 * shortened with copy (01-the-console.md § the moves of 2026-11-01:
 * Settings), and the line where a newer set replaced an earlier one.
 */
export function ShareSet({
  status,
  name,
  replaced,
}: {
  status: EscrowStatus;
  name: string;
  replaced: string | undefined;
}) {
  return (
    <div className="stack">
      <p>
        {setLine(status, name)} <Ident value={status.set ?? ""} label="set id" />
        <span className="small faint"> — {SET_ID_HINT}</span>
      </p>
      {replaced !== undefined && <p className="small muted">{replaced}</p>}
    </div>
  );
}

/**
 * Shares is the three shares in one table, the workspace's own first. The
 * tab's own share — `mine`: the admin slot on Settings, the parent slot on
 * a child's tab — carries the hint while it waits, and the row's acts.
 */
export function Shares({
  status,
  holders,
  mine,
  words,
  busy,
  onCollect,
  onPresent,
}: {
  status: EscrowStatus;
  holders: Holders;
  mine: string;
  words: (r: EscrowSlotStatus) => string;
  busy: boolean;
  onCollect: () => void;
  onPresent: () => void;
}) {
  const mineIn = status.ceremony?.presented.some((p) => p.slot === mine) === true;
  const columns: Column<EscrowSlotStatus>[] = [
    {
      key: "share",
      header: "Share",
      width: "minmax(0, 1fr)",
      value: (r) => SLOT_ORDER[r.slot] ?? 9,
      text: (r) => slotName(r.slot, holders, r.state),
      cell: (r) => <span className="cell-main">{slotName(r.slot, holders, r.state)}</span>,
    },
    {
      key: "held",
      header: "Held by",
      width: "minmax(0, 2fr)",
      sortable: false,
      text: words,
      cell: (r) => (
        <span className="cell">
          <span className="cell-main">{words(r)}</span>
          {r.slot === mine && r.state === SlotState.waiting && (
            <span className="cell-sub">{COLLECT_HINT}</span>
          )}
        </span>
      ),
    },
  ];
  const menu = (r: EscrowSlotStatus): MenuEntry[] => {
    if (r.slot !== mine || busy) {
      return [];
    }
    const entries: MenuEntry[] = [];
    if (r.state === SlotState.waiting) {
      entries.push({ label: "Collect", onSelect: onCollect });
    }
    if (status.ceremony !== undefined && !mineIn) {
      entries.push({ label: "Present", onSelect: onPresent });
    }
    return entries;
  };
  return (
    <Table
      label="Recovery shares"
      rows={status.slots}
      rowKey={(r) => r.slot}
      sort={{ key: "share", dir: "asc" }}
      columns={columns}
      menu={menu}
    />
  );
}

/**
 * Ceremony is the card of an open recovery: why, who started it, which shares
 * are in, when it ends. `who` names a presenter as the tab's shares table
 * names a collector: Settings names its own people, a child's tab names the
 * parent's own and calls the child's *an admin of Acme*.
 */
export function Ceremony({
  status,
  holders,
  who,
}: {
  status: EscrowStatus;
  holders: Holders;
  who: (p: EscrowPresenter | undefined) => string;
}) {
  const c = status.ceremony;
  if (c === undefined) {
    return null;
  }
  return (
    <Notice kind="info" title={ceremonyTitle(holders.name)}>
      <p>
        “{c.reason}” — started by {who(c.opened_by)} on {when(c.opened)}.
      </p>
      <ul className="small">
        {c.presented.map((p) => (
          <li key={p.slot}>
            {slotName(p.slot, holders)} is in, from {who(p.by)} on {when(p.at)}
          </li>
        ))}
      </ul>
      <p className="small">Ends on {when(c.expires)} if no second share arrives.</p>
    </Notice>
  );
}

/** PresentWords are what Present says: its title and line, the field, and whose keys it rebuilds. */
export interface PresentWords {
  title: string;
  line: string;
  /** The share's field: *Acme's share*, *Northwind's share*. */
  field: string;
  /** The workspace whose keys the share rebuilds, by slug and by name. */
  slug: string;
  name: string;
  /** The sentence for a present the service refused, or nothing for a code without one. */
  refusal: (err: unknown) => string | undefined;
}

/** PresentForm is the form, in the tab's place, that hands the tab's own share in. */
export function PresentForm({
  z,
  words,
  opens,
  onClose,
  onDone,
}: {
  z: ZeroTrustState;
  words: PresentWords;
  opens: boolean;
  onClose: () => void;
  onDone: (outcome: string) => void;
}) {
  const [share, setShare] = useState("");
  const [why, setWhy] = useState("");
  const [bad, setBad] = useState<string | undefined>(undefined);
  const [refused, setRefused] = useState<unknown>(undefined);
  const [asking, setAsking] = useState(false);
  const name = words.name;
  const ready = share.trim() !== "" && (!opens || why.trim() !== "");

  const send = () => {
    setRefused(undefined);
    z.present({ share: share.trim(), reason: opens ? why.trim() : undefined }).then(
      (r) => {
        onDone((r as EscrowPresentReply).outcome);
      },
      (e: unknown) => {
        const said = words.refusal(e);
        if (said !== undefined) {
          setBad(said);
        } else {
          setRefused(e);
        }
      },
    );
  };
  const check = () => {
    void checkShare(share).then((c) => {
      const said = checkAnswer(c, words.slug, name);
      setBad(said);
      if (said !== undefined) {
        return;
      }
      if (opens) {
        setAsking(true);
      } else {
        send();
      }
    });
  };
  return (
    <FormPage
      title={words.title}
      line={words.line}
      heading="h2"
      onClose={onClose}
      foot={
        <>
          <Button kind="quiet" onClick={onClose} disabled={z.busy}>
            Cancel
          </Button>
          <Button kind="primary" disabled={z.busy || !ready} onClick={check}>
            {z.busy ? "Presenting…" : "Present"}
          </Button>
        </>
      }
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) {
            check();
          }
        }}
      >
        {bad !== undefined && (
          <Notice kind="bad" title="Not sent">
            {bad}
          </Notice>
        )}
        {refused !== undefined && <NotAllowed err={refused} what="Present" />}
        <Field label={words.field} hint="The line you collected, as you kept it.">
          <TextInput
            value={share}
            onChange={setShare}
            placeholder="impire-recovery-share:…"
            disabled={z.busy}
          />
        </Field>
        {opens && (
          <Field label="Why" hint="Why the keys need rebuilding, in one sentence. It is recorded.">
            <TextArea value={why} onChange={setWhy} disabled={z.busy} />
          </Field>
        )}
      </form>
      {asking && (
        <ConfirmDialog
          ask={{ title: `Start a recovery of ${name}'s keys?`, consequence: presentConsequence(name) }}
          verb="Present"
          danger={false}
          onCancel={() => {
            setAsking(false);
          }}
          onConfirm={() => {
            setAsking(false);
            send();
          }}
        />
      )}
    </FormPage>
  );
}
